import { getScenarioData } from 'mini-host-bridge/lib/utils/getScenarioData';
import { getSupportedFallbackForLocale } from 'owa-shared-bootstrap/lib/getSupportedFallbackForLocale';
import { isGulping } from 'owa-config';
import { isMiniHostAppFeatureEnabled } from 'mini-hostapp-feature-flags/lib/isMiniHostAppFeatureEnabled';
import type { Culture } from 'owa-shared-localization';
import type { OpxHostAppDetailType } from 'mini-opx-config/lib/types/OpxHostAppDetailType';
import type { LiteScenarioData } from 'mini-host-bridge/lib/types/LiteScenarioData';

/**
 * Overrides the culture with en if it's qps-ploc and we're in gulp mode
 * since shared packages don't come with qps-ploc strings
 * qps-ploc represents pseudo-locale names used during development
 * https://docs.microsoft.com/en-gb/windows/win32/intl/pseudo-locales?redirectedfrom=MSDN
 * @param culture
 */
const getLocaleIfGulpAndQPS = (locale: string) => {
    if (locale === 'qps-ploc' && isGulping()) {
        return 'en';
    }

    return locale;
};

/**
 * Returns the localization params based on where mini-owa is loaded.
 * @param locale
 * @param culture
 * @param dir
 */
export const getFinalLocalizationContext = async (
    locale: string,
    culture: Culture,
    dir?: 'ltr' | 'rtl'
) => {
    let computedLocale = getLocaleIfGulpAndQPS(locale);
    let computedCulture = culture;
    let computedDir = dir?.toLocaleLowerCase();

    // return if loadCultureFromHost is not enabled
    if (!isMiniHostAppFeatureEnabled('loadCultureFromHost')) {
        return {
            locale: computedLocale,
            culture: computedCulture,
            dir: computedDir,
        };
    }

    // Get the scenario data passed from the host app
    const scenarioData = await getScenarioData<LiteScenarioData & OpxHostAppDetailType>();

    if (scenarioData.locale) {
        computedLocale = withSupportedFallbackLocale(scenarioData.locale, scenarioData.culture);
    }
    if (scenarioData.userLocaleDirection) {
        computedDir = scenarioData.userLocaleDirection;
    }
    if (scenarioData.culture) {
        computedCulture = scenarioData.culture as Culture;
    }

    // added fallback logic for deviceCulture to support opx scenario when host is not sending locale
    // If deviceCulture is present, we will use it to determine the locale and direction
    if (scenarioData.deviceCulture) {
        const { locale: deviceLocale, dir: deviceLocaleDir } = getSupportedFallbackForLocale(
            scenarioData.deviceCulture
        );
        computedLocale = withSupportedFallbackLocale(deviceLocale, scenarioData.deviceCulture);
        computedDir = deviceLocaleDir;
        computedCulture = scenarioData.deviceCulture as Culture;
    }

    return {
        locale: computedLocale,
        culture: computedCulture,
        dir: computedDir?.toLocaleLowerCase(),
    };
};

// The locale value is case-sensitive and should match the name of a locale directory
const FALLBACK_OVERRIDE: {
    [locale: string]: string;
} = {
    as: 'as-in',
    az: 'az-latn-az',
    bn: 'bn-in',
    bs: 'bs-latn-ba',
    ga: 'ga-ie',
    gd: 'gd-gb',
    in: 'id',
    iw: 'he',
    km: 'km-kh',
    lb: 'lb-lu',
    lo: 'lo-la',
    mi: 'mi-nz',
    mt: 'mt-mt',
    my: 'en',
    nb: 'no',
    ne: 'ne-np',
    nn: 'nn-no',
    pa: 'pa-in',
    quz: 'quz-pe',
    si: 'en',
    ug: 'ug-cn',
};

/**
 * Fallback logic for unsupported locales
 * E.g. Taiwan has a locale code of "zh" which is not supported by the OWA localization pipeline.
 * We fallback to "zh-hant".
 * @param locale
 */
const withSupportedFallbackLocale = (locale: string, culture?: string) => {
    if (locale === 'zh') {
        return culture?.toLowerCase().includes('zh-hans') ? 'zh-hans' : 'zh-hant';
    }

    if (FALLBACK_OVERRIDE.hasOwnProperty(locale)) {
        return FALLBACK_OVERRIDE[locale];
    }

    return locale;
};
